import {
	type CSSProperties,
	Children,
	type ReactElement,
	type ReactNode,
	cloneElement,
	useState
} from 'react';

import { Dialog as ArkDialog } from '@ark-ui/react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// PanelStack.Screen
// ---------------------------------------------------------------------------

interface PanelStackScreenProps {
	children: ReactNode;
	open?: boolean;
	onClose?: () => void;
	/** @internal Injected by PanelStackRoot */
	_depth?: number;
	/** @internal Injected by PanelStackRoot */
	_container?: HTMLDivElement | null;
}

function PanelStackScreen({
	children,
	open,
	onClose,
	_depth = 0,
	_container
}: PanelStackScreenProps) {
	if (_depth === 0) {
		return <div className="pt-panel-stack-base">{children}</div>;
	}

	const content =
		_container &&
		createPortal(
			<ArkDialog.Positioner
				className="pt-panel-stack-positioner"
				style={{ zIndex: `calc(var(--z-panel-stack) + ${_depth})` }}
			>
				<ArkDialog.Content
					className="pt-panel-stack-screen"
					style={{ '--panel-depth': _depth } as CSSProperties}
				>
					{children}
				</ArkDialog.Content>
			</ArkDialog.Positioner>,
			_container
		);

	return (
		<ArkDialog.Root
			open={open}
			onOpenChange={(details) => {
				if (!details.open) onClose?.();
			}}
			modal={false}
			lazyMount
			unmountOnExit
			closeOnInteractOutside
			closeOnEscape
		>
			{content}
		</ArkDialog.Root>
	);
}

// ---------------------------------------------------------------------------
// PanelStack (root)
// ---------------------------------------------------------------------------

interface PanelStackRootProps {
	children: ReactNode;
	className?: string;
}

function PanelStackRoot({ className, children }: PanelStackRootProps) {
	const [container, setContainer] = useState<HTMLDivElement | null>(null);
	const screens = Children.toArray(children).filter(
		(child): child is ReactElement<PanelStackScreenProps> =>
			typeof child === 'object' && child !== null && 'props' in (child as object)
	);

	return (
		<div ref={setContainer} className={cn('pt-panel-stack-root', className)}>
			{screens.map((screen, index) =>
				cloneElement(screen, { _depth: index, _container: container })
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Compound export
// ---------------------------------------------------------------------------

const PanelStack = Object.assign(PanelStackRoot, {
	Screen: PanelStackScreen
});

export { PanelStack, type PanelStackScreenProps };
